import type { AreaRegion } from "@corklog/shared";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { layoutMap } from "../area-map.js";
import {
  isOutlineFile,
  outlinePaths,
  type OutlineFile,
  type OutlinePaths,
} from "../area-outline.js";

/**
 * An area report's map: each region's centre as a point, over the land it is
 * on — tasks 10.35 and 10.37, decided 2026-09-28 in `project-scope.md`.
 *
 * **The points are the model's; everything under them is not.** The land,
 * the borders between countries, the coast, the lakes, the rivers and the
 * provincial lines come from Natural Earth, cut per country by
 * `scripts/build-area-outlines.mjs`. **No wine-area borders**, because the
 * model would invent them and an invented border looks exactly as believable
 * as a real one; where the country is known the map now shows the real lines
 * a reader finds a region by — the Marne, the border with Luxembourg — and a
 * point on the wrong side of one is the model's mistake made visible.
 *
 * **The outlines are fetched, not bundled**: one file for the report's
 * country, only when a map is drawn. Until it arrives, or where there is no
 * file — a country not in the script's list — the map is the graticule and
 * the points, which is complete without them.
 *
 * Drawn in the theme's own tokens through classes, so it follows the device
 * between light and dark like every other surface: the sea, the lakes and
 * the rivers in the water tokens — the interface's one blue, since
 * 2026-09-29, when the raised grey the sea was first drawn in read as land —
 * the report's country the page's surface, its neighbours the ground between.
 * Every shape has an explicit fill or stroke; nothing is left to the SVG
 * default of black.
 *
 * **The larger towns** are drawn too, since 2026-09-29: a small square in ink
 * and the name beside it, upright and smaller than a region's — a region is a
 * disc in the accent colour, so the two are not mistaken for each other.
 *
 * **A click makes it larger**, also since 2026-09-29: the map in its column
 * beside the regions is some twenty rem wide, which holds the points but not
 * much reading of the towns and rivers. The same drawing opens in a native
 * `<dialog>`, as large as the screen allows, and closes on a click anywhere,
 * on Escape or on its close button — no library, and the browser handles the
 * focus and the back gesture.
 */
export function AreaMap({
  regions,
  countryCode,
}: {
  regions: readonly AreaRegion[];
  countryCode: string;
}) {
  const { t } = useTranslation();
  const layout = useMemo(() => layoutMap(regions), [regions]);
  const [file, setFile] = useState<OutlineFile | null>(null);

  useEffect(() => {
    let current = true;
    setFile(null);
    void fetch(`/outlines/${encodeURIComponent(countryCode)}.json`)
      .then((response) => (response.ok ? response.json() : null))
      .then((body: unknown) => {
        if (current && isOutlineFile(body)) setFile(body);
      })
      // No outlines is a map without them, and nothing to say.
      .catch(() => undefined);
    return () => {
      current = false;
    };
  }, [countryCode]);

  const outlines = useMemo(() => {
    if (file === null || layout === null) return null;
    // The regions' points and labels, which the water names must leave clear:
    // the same boxes `placeLabels` judged them by.
    const obstacles = layout.points.flatMap((point) => {
      const width = point.label.length * 7.2;
      const left = point.anchor === "start" ? point.labelX : point.labelX - width;
      return [
        {
          left: point.x - 17,
          right: point.x + 17,
          top: point.y - 17,
          bottom: point.y + 17,
        },
        { left, right: left + width, top: point.labelY - 13, bottom: point.labelY + 3 },
      ];
    });
    return outlinePaths(
      file,
      countryCode,
      layout.bounds,
      layout.project,
      { width: layout.width, height: layout.height },
      obstacles,
    );
  }, [file, layout, countryCode]);

  const dialog = useRef<HTMLDialogElement>(null);

  if (layout === null) return null;
  const label = t("area.mapLabel", {
    regions: layout.points.map((p) => p.label).join(", "),
  });

  return (
    <figure className="space-y-2">
      <button
        type="button"
        onClick={() => dialog.current?.showModal()}
        aria-label={t("area.mapEnlarge")}
        title={t("area.mapEnlarge")}
        className="block w-full cursor-zoom-in"
      >
        <MapDrawing layout={layout} outlines={outlines} label={label} />
      </button>
      <figcaption className="text-muted text-xs">{t("area.mapCaption")}</figcaption>
      <dialog
        ref={dialog}
        onClick={() => dialog.current?.close()}
        aria-label={label}
        className="bg-surface m-auto max-h-none max-w-none cursor-zoom-out rounded-sm p-2 backdrop:bg-black/60"
      >
        <div
          style={{
            // As large as the screen allows, keeping the drawing's proportions.
            width: `min(94vw, calc(82vh * ${(layout.width / layout.height).toFixed(3)}))`,
          }}
        >
          <MapDrawing layout={layout} outlines={outlines} label={label} />
          <div className="mt-2 flex items-center justify-between gap-3">
            <p className="text-muted text-xs">{t("area.mapCaption")}</p>
            <button
              type="button"
              onClick={() => dialog.current?.close()}
              className="text-accent shrink-0 text-sm underline-offset-4 hover:underline"
            >
              {t("area.mapClose")}
            </button>
          </div>
        </div>
      </dialog>
    </figure>
  );
}

type Layout = NonNullable<ReturnType<typeof layoutMap>>;

/** The drawing itself, the same in the column and in the enlarged dialog. */
function MapDrawing({
  layout,
  outlines,
  label,
}: {
  layout: Layout;
  outlines: OutlinePaths | null;
  label: string;
}) {
  const { t } = useTranslation();
  const { width, height, points, latitudes, longitudes, scale } = layout;
  return (
    <svg
      viewBox={`0 0 ${String(width)} ${String(height)}`}
      role="img"
      aria-label={label}
      className={`${outlines === null ? "bg-surface" : "bg-water"} border-line h-auto w-full rounded-sm border`}
    >
      {outlines !== null && (
        <g>
          <path
            d={outlines.neighbours}
            className="fill-ground stroke-line"
            strokeWidth={0.8}
          />
          <path
            d={outlines.home}
            className="fill-surface stroke-muted"
            strokeWidth={0.8}
          />
          <path
            d={outlines.lakes}
            className="fill-water stroke-river"
            strokeWidth={0.6}
          />
          <path
            d={outlines.admin}
            className="stroke-line"
            fill="none"
            strokeWidth={0.8}
            strokeDasharray="3 3"
          />
          <path
            d={outlines.rivers}
            className="stroke-river"
            fill="none"
            strokeWidth={1.3}
            strokeOpacity={0.8}
            strokeLinejoin="round"
          />
          {/* The rivers' and lakes' names: italic, in the water's own blue,
              smaller than a region's — the convention of every atlas, and
              what keeps them from being read as regions. */}
          {outlines.labels.map((label) => (
            <text
              key={`${label.text}-${label.x.toFixed(0)}`}
              x={label.x}
              y={label.y}
              textAnchor="middle"
              transform={`rotate(${label.angle.toFixed(1)} ${label.x.toFixed(1)} ${label.y.toFixed(1)})`}
              className="fill-river text-[10px] italic"
            >
              {label.text}
            </text>
          ))}
          {/* The towns: a square in ink and an upright name, smaller than a
              region's, so neither is read as the other. */}
          {outlines.towns.map((town) => (
            <g key={`${town.text}-${town.x.toFixed(0)}`}>
              <rect
                x={town.x - 2.5}
                y={town.y - 2.5}
                width={5}
                height={5}
                className="fill-ink stroke-surface"
                strokeWidth={1}
              />
              <text
                x={town.labelX}
                y={town.labelY}
                textAnchor={town.anchor}
                className="fill-ink text-[10px]"
              >
                {town.text}
              </text>
            </g>
          ))}
        </g>
      )}
      {latitudes.map((line) => (
        <g key={`lat-${line.label}`}>
          <line x1={0} x2={width} y1={line.at} y2={line.at} className="stroke-line" />
          <text x={6} y={line.at - 4} className="fill-muted text-[10px] tabular-nums">
            {line.label}
          </text>
        </g>
      ))}
      {longitudes.map((line) => (
        <g key={`lng-${line.label}`}>
          <line x1={line.at} x2={line.at} y1={0} y2={height} className="stroke-line" />
          <text
            x={line.at + 4}
            y={height - 6}
            className="fill-muted text-[10px] tabular-nums"
          >
            {line.label}
          </text>
        </g>
      ))}

      {points.map((point) => (
        <g key={point.label}>
          <circle cx={point.x} cy={point.y} r={16} className="fill-accent opacity-15" />
          <circle cx={point.x} cy={point.y} r={5} className="fill-accent" />
          <text
            x={point.labelX}
            y={point.labelY}
            textAnchor={point.anchor}
            className="fill-ink font-display text-[13px] font-semibold"
          >
            {point.label}
          </text>
        </g>
      ))}

      {/* Top right, where no latitude label stands. */}
      <g transform={`translate(${String(width - 16 - scale.length)}, 18)`}>
        <line
          x1={0}
          x2={scale.length}
          y1={0}
          y2={0}
          className="stroke-ink"
          strokeWidth={2}
        />
        <text x={0} y={14} className="fill-muted text-[10px] tabular-nums">
          {t("area.mapScale", { km: scale.km })}
        </text>
      </g>
    </svg>
  );
}
